import React from 'react'
import { i18n } from '@/locale/i18n'
import { formatNumber } from '@/util/localizeNumber'
import PersonIcon from '@/assets/icons/person'
import CommentsIcon from '@/assets/icons/comments'
import LiquidityIcon from '@/assets/icons/liquidity'
import { OomsIcon, RealMoneyIcon, StatsIcon } from '@/assets/icons'

/**
 * The market's stats side by side for both currencies — traders, volume, liquidity,
 * comments. Held apart from the surface that opens it — the card footer does so from its
 * stats row — so a market never reports two different sets of numbers.
 */
const MarketStatsTooltipContent = ({ question, locale }) => {
  if (!question) return null

  const count = value => (value == null ? '—' : formatNumber(value, locale, 0, 0))
  const realMoney = value => (value == null ? '—' : `$ ${formatNumber(value, locale, 0, 0)}`)
  const playMoney = value => (value == null ? '—' : `${formatNumber(value, locale, 0, 0)} ø`)

  const renderRow = (icon, label, realValue, playValue) => (
    <>
      <span className="flex items-center gap-2 text-Neutral-6 whitespace-nowrap">
        {icon}
        {label}
      </span>
      <span className="text-right font-semibold text-Neutral-6 whitespace-nowrap">{realValue}</span>
      <span className="text-right font-semibold text-Neutral-6 whitespace-nowrap">{playValue}</span>
    </>
  )

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-3 gap-y-2.5 items-center w-full min-w-0 self-stretch text-sm">
      <span />
      <span className="flex items-center justify-end gap-1 font-bold text-Neutral-6 whitespace-nowrap">
        <RealMoneyIcon width="18px" />
        {i18n('Real_money')}
      </span>
      <span className="flex items-center justify-end gap-1 font-bold text-Neutral-6 whitespace-nowrap">
        <OomsIcon width="18px" />
        {i18n('Play_money')}
      </span>

      {renderRow(
        <PersonIcon fillcolor="currentColor" width="14px" />,
        i18n('question_card.traders'),
        count(question.wagers_count_canonical),
        count(question.wagers_count)
      )}
      {renderRow(
        <StatsIcon fillcolor="currentColor" width="14px" />,
        i18n('question_card.volume'),
        realMoney(question.volume_real_money),
        playMoney(question.volume_play_money)
      )}
      {renderRow(
        <LiquidityIcon fillcolor="currentColor" width="14px" />,
        i18n('question_card.liquidity'),
        realMoney(question.liquidity_real_money),
        playMoney(question.liquidity_play_money)
      )}

      <div className="col-span-3 border-t border-solid border-Neutral-2 my-0.5" />

      <span className="flex items-center gap-2 text-Neutral-6 whitespace-nowrap">
        <CommentsIcon width="14" height="14" fill="currentColor" />
        {i18n('question_card.comments')}
      </span>
      <span className="col-span-2 text-right font-semibold text-Neutral-6 whitespace-nowrap">
        {count(question.comment_count)}
      </span>
    </div>
  )
}

export default MarketStatsTooltipContent
